"use client";

import { useTranslation } from "next-i18next";
import { useEffect, useState, useMemo } from "react";
import ReactTextTransition from "react-text-transition";
import { useRecoilState } from "recoil";

import BrokerConnectButton from "./BrokerConnectButton";
import { brokerConnectModalAtom } from "@/Atoms/modal/DialogueAtoms";
import { useUserContext } from "@/contexts/userContext";
import { formatCurrency } from "@/lib/utils";
import { brokersList } from "./blocks/connection/BrokerList";
import { Skeleton } from "./ui/skeleton";
import { useAppDetails } from "@/contexts/AppDetailsContext";

const Funds = () => {
  const { t } = useTranslation("layout-page");
  const { connectedBrokers, balanceLoading } = useUserContext();
  const [showConnectForm, setShowConnectForm] = useRecoilState(
    brokerConnectModalAtom,
  );
  const [currentBrokerIndex, setCurrentBrokerIndex] = useState(0);
  const brokerIds = useMemo(
    () => Object.keys(connectedBrokers).map((id) => Number(id)) ?? [12],
    [connectedBrokers],
  );
  const { details } = useAppDetails();
  const currentBrokerId = details?.isWhiteLabled
    ? Number(details?.id)
    : brokerIds[currentBrokerIndex];
  const currentBalance = currentBrokerId
    ? (connectedBrokers[currentBrokerId]?.balance ?? 0)
    : 0;

  useEffect(() => {
    if (currentBrokerIndex >= brokerIds.length && brokerIds.length > 0) {
      setCurrentBrokerIndex(0);
    }
  }, [brokerIds.length, currentBrokerIndex]);

  useEffect(() => {
    if (!brokerIds.length) return;

    let index = 0;
    const interval = setInterval(() => {
      setCurrentBrokerIndex((prev) => {
        index = (prev + 1) % brokerIds.length;
        return index;
      });
    }, 5000);

    return () => clearInterval(interval);
  }, [brokerIds]); // ✅ only depend on length, not full array

  if (balanceLoading) {
    return (
      <div className="flex justify-center items-center cursor-pointer">
        <Skeleton className="h-[40px] w-[150px]" />
      </div>
    );
  }
  if (brokerIds.length == 0 && !balanceLoading) {
    return <BrokerConnectButton />;
  }

  return (
    <div
      className="cursor-pointer flex flex-col gap-0 items-start justify-start max-h-[80px] w-auto"
      onClick={() => setShowConnectForm({ isOpen: true })}
    >
      <div className="flex items-center px-0.5 ">
        <div className="flex items-center ">
          <div className="w-[10px] h-[10px] rounded-xl bg-[--marketplace-green]"></div>
          <div>
            <p className="text-muted-foreground text-xs font-bold  ml-2">
              {details?.isWhiteLabled
                ? t("Account Connected")
                : t("Connected Brokers")}
            </p>
          </div>
        </div>
      </div>

      <div className="flex font-bold text-sm  text-muted-foreground items-center gap-2 ">
        <p
          className={`flex w-full items-center h-[25px] justify-start gap-2 font-bold text-xs ${
            currentBalance > 0
              ? "text-[--marketplace-green]"
              : currentBalance < 0
                ? "text-[--marketplace-red]"
                : ""
          }`}
        >
          <BalanceDisplay
            value={formatCurrency(currentBalance, 2, false, undefined, true)}
            brokerId={currentBrokerId}
            multipleBrokers={brokerIds.length > 1}
          />
        </p>
      </div>
    </div>
  );
};

export default Funds;

interface BalanceDisplayProps {
  value: string;
  brokerId?: number;
  multipleBrokers: boolean;
}

const BalanceDisplay = ({
  value,
  brokerId,
  multipleBrokers,
}: BalanceDisplayProps) => {
  const broker =
    brokerId !== undefined
      ? brokersList.find((b) => Number(b.id) === brokerId)
      : undefined;
  const { details } = useAppDetails();

  if (details?.isWhiteLabled) {
    return (
      <div title={value} className="flex items-center gap-2 h-[25px] ">
        {broker?.icon && (
          <div className="flex gap-1">
            <p className="text-white">Balance</p>
          </div>
        )}
        <p className="text-[14px] font-semibold">{value}</p>
      </div>
    );
  }

  if (!multipleBrokers) {
    return (
      <div title={value} className="flex items-center gap-2 h-[25px] ">
        {broker?.icon && (
          <div className="flex gap-1">
            <img
              src={broker.icon}
              alt={broker.name ?? ""}
              className=" h-[15px]   "
            />
            <p className="text-white">{broker?.name}</p>
          </div>
        )}
        <p className="text-[14px] font-semibold">{value}</p>
      </div>
    );
  }

  return (
    <ReactTextTransition className="h-[25px] flex items-center" inline>
      <div title={value} className="flex items-center gap-2 ">
        {broker?.icon && (
          <div className="flex gap-1 mr-2">
            <img
              src={broker.icon}
              alt={broker.name ?? ""}
              className=" h-[15px]"
            />
            <p className="text-white">{broker?.name}</p>
          </div>
        )}
        <p className="text-[14px] font-semibold ml-2">{value}</p>
      </div>
    </ReactTextTransition>
  );
};
